import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { createStatement, fetchStatement, fetchStatements, startStatement } from './api';
import type { CreateStatementInput } from './types';

export const statementsKeys = {
  all: ['acc-statements'] as const,
  list: (params?: { pageNumber?: number; pageLength?: number }) =>
    [...statementsKeys.all, 'list', params] as const,
  detail: (id: string) => [...statementsKeys.all, 'detail', id] as const,
};

export function useStatements(params: { pageNumber?: number; pageLength?: number } = {}) {
  return useQuery({
    queryKey: statementsKeys.list(params),
    queryFn: () => fetchStatements(params),
  });
}

export function useStatement(id: string | null) {
  return useQuery({
    queryKey: statementsKeys.detail(id ?? ''),
    queryFn: () => fetchStatement(id!),
    enabled: !!id,
  });
}

export function useCreateStatement() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateStatementInput) => createStatement(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: statementsKeys.all });
      toast.success('Reconciliation started');
    },
    onError: (error: Error) => {
      toast.error('Failed to start reconciliation', { description: error.message });
    },
  });
}

/**
 * Re-run the QB audit for an existing statement via POST /v4/acc-statements/:id/start.
 * Invalidates the statements list so the match counts refresh on success.
 * `variables` (the statement id) is exposed so callers can show a per-row
 * pending state while the mutation is in flight.
 */
export function useStartStatement() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => startStatement(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: statementsKeys.all });
      toast.success('Audit re-run started');
    },
    onError: (error: Error) => {
      toast.error('Failed to re-run audit', { description: error.message });
    },
  });
}
